import React from 'react';
import { Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult } from '../types/advancedLifeAnalysis';
import { getNatalPromiseTier, getTimingConfluence, calculateProjectedAgeWindow } from '../utils/presentationHelper';
import { Zap, Clock, Compass, CheckCircle2, Shield, Calendar, Sparkles } from 'lucide-react';

interface NatalPromiseVerdictCardProps {
  analysisResult: AdvancedLifeAnalysisResult;
  lang: Language;
  dobString?: string;
  className?: string;
}

export const NatalPromiseVerdictCard: React.FC<NatalPromiseVerdictCardProps> = ({
  analysisResult,
  lang,
  dobString = '1990-05-15',
  className = ''
}) => {
  const isHi = lang === 'hi';
  const { natalPromise, phalaBhutaWindows, lifeStageContext } = analysisResult;
  const primaryWindow = phalaBhutaWindows.primary;

  const timingConfluence = getTimingConfluence(primaryWindow.confidenceScore, lang);
  const projectedAgeStr = calculateProjectedAgeWindow(
    dobString,
    primaryWindow.startDate,
    primaryWindow.endDate,
    lang
  );

  return (
    <div
      id="natal-promise-verdict-card"
      className={`grid grid-cols-1 lg:grid-cols-3 gap-5 ${className}`}
    >
      {/* 1. Natal Promise Qualitative Verdict Card (Answer-First & 3-Tier Human-First Standard) */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs flex flex-col justify-between space-y-4">
        <div>
          {/* Header Row: Qualitative Tier Dominant + Secondary Analytical Index */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
              {isHi ? 'मुख्य जीवन संकेत' : 'What Stands Out in Your Life'}
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-mono font-semibold bg-astro-card text-astro-text border border-astro-border">
              {isHi ? `विश्लेषणात्मक सूचकांक: ${natalPromise.score}/100` : `Analytical Index: ${natalPromise.score}/100`}
            </span>
          </div>

          {/* Dominant Qualitative Tier Badge: STRONG MOMENTUM */}
          <div className="mb-3">
            <span className="inline-block px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-extrabold tracking-wide uppercase bg-astro-primary/15 text-astro-primary border border-astro-primary/30">
              {isHi ? 'प्रबल गतिशीलता (Strong Momentum)' : 'Strong Momentum'}
            </span>
          </div>

          {/* Tier 1 (Human Meaning): Large Headline + Conversational Description */}
          <h4 className="text-base sm:text-lg font-bold text-astro-text font-serif mb-2 leading-snug">
            {isHi ? natalPromise.verdictHi : natalPromise.verdictEn}
          </h4>
          <p className="text-xs sm:text-sm text-astro-secondary-text leading-relaxed font-normal mb-4">
            {isHi ? natalPromise.analysisSummaryHi : natalPromise.analysisSummaryEn}
          </p>

          {/* Tier 2 (Technical Badge): Astrological technical anchor */}
          <div className="mb-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-mono bg-astro-card text-astro-primary border border-astro-border">
              <Sparkles className="w-3 h-3 text-astro-accent" />
              <span>Rajayoga Alignment • {natalPromise.score}% Support Score</span>
            </span>
          </div>

          {/* Tier 3 (Classical Reference): Italicized, muted footnote */}
          <div className="text-[11px] text-astro-secondary-text italic">
            Ref: BPHS Ch. 35
          </div>
        </div>

        {/* Life-Stage Context Anchor */}
        <div className="pt-3 border-t border-astro-border flex items-center justify-between text-xs text-astro-secondary-text">
          <span>{isHi ? 'संबद्ध वर्ग चक्र:' : 'Thematic Vargas:'}</span>
          <span className="font-semibold text-astro-primary font-mono">
            {analysisResult.relevantVargas.join(', ')}
          </span>
        </div>
      </div>

      {/* 2. Primary Manifestation Window Card (Span 2) */}
      <div className="lg:col-span-2 bg-astro-card border-2 border-astro-primary/30 rounded-2xl p-5 sm:p-6 shadow-xs relative overflow-hidden flex flex-col justify-between">
        <div>
          {/* Header Row: Primary Window Tag + Confluence Stars Rating */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-astro-primary text-white rounded-full text-xs font-bold tracking-wide shadow-2xs">
              <Zap className="w-3.5 h-3.5 text-astro-accent" />
              {isHi ? 'प्राथमिक फल-प्रदाता काल' : 'Primary Fructification Window'}
            </div>

            {/* Locked Confluence Rating (No empirical % probability) */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-astro-bg border border-astro-border text-xs font-bold">
              <span className="text-astro-accent font-mono tracking-wider">{timingConfluence.stars}</span>
              <span className={timingConfluence.textColor}>{isHi ? timingConfluence.labelHi : timingConfluence.labelEn}</span>
            </div>
          </div>

          {/* Window Dates & Projected Native Age */}
          <div className="flex flex-wrap items-baseline gap-2 mb-3">
            <span className="text-xl sm:text-2xl font-bold font-serif text-astro-text">
              {primaryWindow.startDate}
            </span>
            <span className="text-sm font-medium text-astro-secondary-text">
              {isHi ? 'से' : 'to'}
            </span>
            <span className="text-xl sm:text-2xl font-bold font-serif text-astro-text">
              {primaryWindow.endDate}
            </span>

            {/* Projected Native Age Pill */}
            {projectedAgeStr && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-md bg-astro-bg border border-astro-border text-astro-primary ml-1 shadow-2xs">
                <Calendar className="w-3 h-3 text-astro-primary shrink-0" />
                <span>{projectedAgeStr}</span>
              </span>
            )}

            {/* Dasha Lords */}
            <span className="text-xs font-bold px-2.5 py-0.5 rounded bg-astro-bg text-astro-primary border border-astro-border">
              {primaryWindow.mahadashaLord}–{primaryWindow.antardashaLord}
            </span>
          </div>

          {/* Plain Language Astrological Mechanism */}
          <p className="text-xs sm:text-sm text-astro-text leading-relaxed mb-4 font-serif">
            {isHi ? primaryWindow.astrologicalMechanismHi : primaryWindow.astrologicalMechanismEn}
          </p>

          {/* Key Supporting Convergences */}
          <div className="space-y-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-astro-secondary-text">
              {isHi ? 'मुख्य शास्त्रीय संमिलन प्रमाण:' : 'Key Astrological Confluences:'}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {primaryWindow.supportingEvidence.map((ev, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] bg-astro-bg border border-astro-border text-astro-text font-medium shadow-2xs"
                >
                  <CheckCircle2 className="w-3 h-3 text-astro-primary shrink-0" />
                  <span>{ev}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Alternate Window Notice if present */}
        {phalaBhutaWindows.alternate && (
          <div className="mt-4 pt-3 border-t border-astro-border flex flex-wrap items-center justify-between text-xs text-astro-secondary-text gap-2">
            <span className="font-semibold text-astro-primary">
              {isHi ? 'वैकल्पिक फल काल:' : 'Alternate Window:'} {phalaBhutaWindows.alternate.startDate} {isHi ? 'से' : 'to'} {phalaBhutaWindows.alternate.endDate} ({phalaBhutaWindows.alternate.mahadashaLord}–{phalaBhutaWindows.alternate.antardashaLord})
            </span>
            <span className="text-[11px] bg-astro-bg px-2 py-0.5 rounded font-mono font-bold text-astro-accent border border-astro-border">
              {getTimingConfluence(phalaBhutaWindows.alternate.confidenceScore, lang).stars}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
